/**
 * Data layer for the document editor's position picker (sidebar quick search + „Erweiterte Suche“):
 * fetches Materialien and Leistungen side by side and normalises both into one row shape.
 */

export type PickerItemKind = 'material' | 'leistung';

export type PickerItem = {
  kind: PickerItemKind;
  id: string;
  name: string;
  unit: string | null;
  gross_price: number | null;
  description: string | null;
  folder_id: string | null;
  article_number: string | null;
  /** Großhändler / Hersteller (materials only). */
  origin: string | null;
  /** Einkaufspreis (materials: net_price, Leistungen: net_cost). */
  ek: number | null;
  minutes: number | null;
  metal_kind: 'cu' | 'al' | null;
  metal_kg_per_unit: number | null;
  /** Relevance from the shared reranker (semantic search only); comparable across both kinds. */
  score: number | null;
};

export type PickerFolder = { id: string; name: string; count: number; datanorm: boolean };

export type PickerSupplier = { id: string; name: string; count: number };

/** keyword = ILIKE on name / Art.-Nr. (exact), semantic = best matches by meaning (reranked). */
export type PickerSearchMode = 'keyword' | 'semantic';

export type PickerResult = {
  items: PickerItem[];
  /** Server-side total when known (browse / keyword with paging), else items.length. */
  total: number;
};

export const PICKER_PAGE_SIZE = 100;

const finiteOrNull = (value: unknown): number | null =>
  value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;

const textOrNull = (value: unknown): string | null => {
  const s = typeof value === 'string' ? value.trim() : '';
  return s || null;
};

export function normalizePickerItem(kind: PickerItemKind, raw: Record<string, unknown>): PickerItem {
  const metalKind = raw.metal_kind === 'cu' || raw.metal_kind === 'al' ? raw.metal_kind : null;
  return {
    kind,
    id: String(raw.id),
    name: String(raw.name ?? ''),
    unit: textOrNull(raw.unit),
    gross_price: finiteOrNull(raw.gross_price),
    description: textOrNull(raw.description),
    folder_id: textOrNull(raw.folder_id),
    article_number: textOrNull(raw.article_number) ?? (kind === 'material' ? textOrNull(raw.gtin) : null),
    origin:
      kind === 'material'
        ? textOrNull(raw.supplier_name) ?? textOrNull(raw.supplier) ?? textOrNull(raw.brand)
        : null,
    ek: finiteOrNull(raw.net_price) ?? finiteOrNull(raw.net_cost) ?? finiteOrNull(raw.purchase_price),
    minutes: finiteOrNull(raw.minutes),
    metal_kind: metalKind,
    metal_kg_per_unit: finiteOrNull(raw.metal_kg_per_unit),
    score: finiteOrNull(raw.score),
  };
}

/** How well a keyword hit matches: exact Art.-Nr. > name starts with > word start > contained. */
function keywordMatchRank(item: PickerItem, query: string): number {
  const q = query.trim().toLocaleLowerCase('de');
  if (!q) return 0;
  const article = (item.article_number ?? '').toLocaleLowerCase('de');
  const name = item.name.toLocaleLowerCase('de');
  if (article && article === q) return 5;
  if (name === q) return 4;
  if (name.startsWith(q) || (article && article.startsWith(q))) return 3;
  if (name.split(/[\s(/,.-]+/).some((word) => word.startsWith(q))) return 2;
  if (name.includes(q) || article.includes(q)) return 1;
  return 0;
}

/**
 * One list across both kinds, best match first. Semantic results carry reranker scores from the
 * same model, so they sort directly. Keyword results have none: they are interleaved by server
 * rank and then ordered by match quality (stable, so the server order breaks ties).
 */
export function mergePickerResults(lists: PickerItem[][], query = ''): PickerItem[] {
  const nonEmpty = lists.filter((l) => l.length > 0);
  if (nonEmpty.length === 0) return [];
  const allScored = nonEmpty.every((l) => l.every((i) => i.score != null));
  if (allScored) return nonEmpty.flat().sort((a, b) => (b.score as number) - (a.score as number));
  const merged: PickerItem[] = [];
  const longest = Math.max(...nonEmpty.map((l) => l.length));
  for (let i = 0; i < longest; i += 1) {
    for (const list of nonEmpty) if (i < list.length) merged.push(list[i]);
  }
  if (!query.trim()) return merged;
  return merged
    .map((item, index) => ({ item, index, rank: keywordMatchRank(item, query) }))
    .sort((a, b) => b.rank - a.rank || a.index - b.index)
    .map((e) => e.item);
}

export function sortPickerItemsByName(items: PickerItem[]): PickerItem[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { numeric: true, sensitivity: 'base' }));
}

async function getJson(url: string, signal?: AbortSignal): Promise<unknown> {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error || 'Laden fehlgeschlagen');
  }
  return res.json();
}

function rowsOf(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  const items = (data as { items?: unknown } | null)?.items;
  return Array.isArray(items) ? (items as Record<string, unknown>[]) : [];
}

function totalOf(data: unknown, fallback: number): number {
  const d = data as { total_count?: unknown; total?: unknown } | null;
  return finiteOrNull(d?.total_count) ?? finiteOrNull(d?.total) ?? fallback;
}

const listPath = (kind: PickerItemKind) => (kind === 'material' ? '/api/materials' : '/api/leistungen');
const searchPath = (kind: PickerItemKind) =>
  kind === 'material' ? '/api/materials/search' : '/api/leistungen/search';

/**
 * One kind, one page. Empty query or keyword mode → list endpoint (supports folder, Großhändler,
 * paging and an exact total). Semantic mode → search endpoint (ranked, single page).
 */
export async function fetchPickerItems(opts: {
  kind: PickerItemKind;
  query: string;
  mode: PickerSearchMode;
  folderId?: string | null;
  supplierId?: string | null;
  page?: number;
  signal?: AbortSignal;
}): Promise<PickerResult> {
  const { kind, mode, folderId, supplierId, signal } = opts;
  const query = opts.query.trim();
  const page = opts.page ?? 1;

  if (query && mode === 'semantic') {
    const p = new URLSearchParams({ q: query });
    if (kind === 'material' && folderId) p.set('folder_id', folderId);
    if (kind === 'material' && supplierId) p.set('supplier_id', supplierId);
    const data = await getJson(`${searchPath(kind)}?${p.toString()}`, signal);
    let items = rowsOf(data).map((r) => normalizePickerItem(kind, r));
    // /api/leistungen/search has no folder filter.
    if (kind === 'leistung' && folderId) items = items.filter((i) => i.folder_id === folderId);
    return { items, total: items.length };
  }

  const p = new URLSearchParams({
    with_meta: '1',
    limit: String(PICKER_PAGE_SIZE),
    page: String(page),
  });
  if (query) p.set('q', query);
  if (folderId) p.set('folder_id', folderId);
  if (kind === 'material' && supplierId) p.set('supplier_id', supplierId);
  const data = await getJson(`${listPath(kind)}?${p.toString()}`, signal);
  const items = rowsOf(data).map((r) => normalizePickerItem(kind, r));
  return { items, total: totalOf(data, items.length) };
}

/**
 * Sidebar quick search: keyword (ILIKE / trigram) — predictable for names, Art.-Nr. and cable
 * codes, and much faster than the semantic endpoint while typing.
 */
export async function quickSearchPickerItems(
  kind: PickerItemKind,
  query: string,
  signal?: AbortSignal,
): Promise<PickerResult> {
  const p = new URLSearchParams({ q: query.trim(), mode: 'keyword' });
  const data = await getJson(`${searchPath(kind)}?${p.toString()}`, signal);
  const items = rowsOf(data).map((r) => normalizePickerItem(kind, r));
  return { items, total: totalOf(data, items.length) };
}

export async function fetchPickerFolders(): Promise<Record<PickerItemKind, PickerFolder[]>> {
  const [mf, lf] = await Promise.all([
    getJson('/api/material-folders').catch(() => []),
    getJson('/api/leistung-folders').catch(() => []),
  ]);
  const toFolders = (raw: unknown, countKey: string): PickerFolder[] =>
    rowsOf(raw)
      .map((f) => ({
        id: String(f.id),
        name: String(f.name ?? ''),
        count: Number(f[countKey]) || 0,
        datanorm: f.section === 'datanorm',
        section: String(f.section ?? ''),
      }))
      .sort((a, b) => a.section.localeCompare(b.section) || a.name.localeCompare(b.name, 'de'))
      .map(({ section: _section, ...folder }) => folder);
  return { material: toFolders(mf, 'material_count'), leistung: toFolders(lf, 'leistung_count') };
}

export async function fetchPickerSuppliers(): Promise<PickerSupplier[]> {
  const data = await getJson('/api/suppliers').catch(() => ({ items: [] }));
  return rowsOf(data)
    .map((s) => ({ id: String(s.id), name: String(s.name ?? ''), count: Number(s.material_count) || 0 }))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export const pickerEurFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
export const pickerNumFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

/** Artikelnummer und Herkunft trennen die vielen fast gleich benannten Katalogartikel. */
export function pickerIdentityLabel(item: PickerItem): string {
  return [item.article_number, item.origin].filter(Boolean).join(' · ');
}

export function pickerPricingLabel(item: PickerItem): string {
  const parts: string[] = [];
  if (item.unit) parts.push(item.unit);
  if (item.ek != null) parts.push(`EK ${pickerEurFmt.format(item.ek)}`);
  if (item.gross_price != null) parts.push(`VK ${pickerEurFmt.format(item.gross_price)}`);
  if (item.minutes != null && item.minutes > 0) parts.push(`${pickerNumFmt.format(item.minutes)} Min`);
  if (item.metal_kind && item.metal_kg_per_unit != null) {
    const kind = item.metal_kind === 'cu' ? 'Cu' : 'Al';
    parts.push(`${kind} ${pickerNumFmt.format(item.metal_kg_per_unit)} kg/${item.unit || 'ME'}`);
  }
  return parts.join(' · ');
}
